import { Link, useNavigate } from "react-router-dom";
import { useState, useRef } from "react";
import { CURRENT_USER, ACTIVITY_TYPES, INTENTS, VIBES, COMMUNITIES, MOCK_LOCAL_LEGENDS, getActivityType } from "@/lib/mockData";
import { Camera, LogOut, Settings, Edit3, TrendingUp, Trophy, Check } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { apiUsers, apiUpload, API_BASE } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { toast } from "sonner";

const Profile = () => {
  const nav = useNavigate();
  const { user, signOut, refresh } = useAuth();
  const fileRef = useRef(null);

  // Merge real user with mock defaults for display-only fields (history, etc.).
  const u = user
    ? {
        ...CURRENT_USER,
        ...user,
        history: user.history && user.history.joined_count ? user.history : CURRENT_USER.history,
        avatar: user.avatar_url || user.avatar || CURRENT_USER.avatar,
        communities: user.communities || [],
        interests: user.interests || [],
        // Backwards-compat: handle legacy string values gracefully.
        default_intent: Array.isArray(user.default_intent) ? user.default_intent : (user.default_intent ? [user.default_intent] : []),
        default_vibe: Array.isArray(user.default_vibe) ? user.default_vibe : (user.default_vibe ? [user.default_vibe] : []),
      }
    : CURRENT_USER;

  const [bio, setBio] = useState(u.bio || "");
  const [editingBio, setEditingBio] = useState(false);
  const [suburb, setSuburb] = useState(u.suburb || "");
  const [editingSuburb, setEditingSuburb] = useState(false);
  const [saving, setSaving] = useState(false);

  const requireAuthed = () => {
    if (!user) {
      toast.info("Sign up to edit your profile");
      return false;
    }
    return true;
  };

  const patch = async (body) => {
    if (!requireAuthed()) return;
    setSaving(true);
    try {
      await apiUsers.updateMe(body);
      await refresh();
      toast.success("Saved", { duration: 1200 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const saveBio = async () => {
    if (bio !== u.bio) await patch({ bio });
    setEditingBio(false);
  };

  const saveSuburb = async () => {
    if (suburb !== u.suburb) await patch({ suburb });
    setEditingSuburb(false);
  };

  const toggleArr = (key, value) => {
    const arr = u[key] || [];
    const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
    patch({ [key]: next });
  };

  const toggleLimited = (key, value, max) => {
    const arr = u[key] || [];
    if (arr.includes(value)) {
      patch({ [key]: arr.filter((x) => x !== value) });
    } else if (arr.length < max) {
      patch({ [key]: [...arr, value] });
    }
  };

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !requireAuthed()) return;
    if (!file.type.startsWith("image/") && !/\.(jpe?g|png|webp|heic|heif|gif)$/i.test(file.name || "")) {
      toast.error("Please pick an image file");
      e.target.value = "";
      return;
    }
    setSaving(true);
    const t = toast.loading("Optimising photo…");
    try {
      // Normalise + resize client-side so iPhone HEIC and huge photos both work.
      let prepared;
      try {
        prepared = await resizeImage(file, { maxEdge: 1600, jpegQuality: 0.85 });
      } catch {
        // Fallback to the original if canvas decode failed (e.g. true HEIC on a non-iOS browser).
        prepared = file;
      }
      if (prepared.size > 6 * 1024 * 1024) {
        // Try one more aggressive pass.
        prepared = await resizeImage(file, { maxEdge: 1200, jpegQuality: 0.75 });
      }
      toast.dismiss(t);
      const res = await apiUpload(prepared);
      const fullUrl = res.url.startsWith("/api")
        ? `${API_BASE.replace(/\/api$/, "")}${res.url}`
        : `${API_BASE}${res.url}`;
      await apiUsers.updateMe({ avatar_url: fullUrl });
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  };

  const handleLogout = async () => {
    await signOut();
    nav("/");
  };

  return (
    <div className="pt-5" data-testid="profile-page">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatar} data-testid="profile-avatar-input" />

      <div className="brutal-card p-5">
        <div className="flex items-start gap-4">
          <div className="relative">
            <img src={u.avatar} className="w-20 h-20 rounded-2xl object-cover brutal-border brutal-shadow-sm" alt={u.name} />
            <button
              onClick={() => requireAuthed() && fileRef.current?.click()}
              className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#FFCC00] brutal-border rounded-full flex items-center justify-center"
              data-testid="profile-avatar-edit"
              disabled={saving}
            >
              <Camera className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex-1">
            <h1 className="font-heading font-black text-2xl leading-tight">{u.name}{u.age ? `, ${u.age}` : ""}</h1>
            {editingSuburb ? (
              <input
                autoFocus
                value={suburb}
                onChange={(e) => setSuburb(e.target.value)}
                onBlur={saveSuburb}
                onKeyDown={(e) => e.key === "Enter" && saveSuburb()}
                className="brutal-input text-sm py-1 mt-1 max-w-[180px]"
                data-testid="profile-suburb-input"
              />
            ) : (
              <button onClick={() => requireAuthed() && setEditingSuburb(true)} className="text-sm text-[#555555] underline-offset-2 hover:underline" data-testid="profile-suburb-display">
                {u.suburb || "Add suburb"}
              </button>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <Link to="/tokens" className="brutal-border bg-[#FFFBEB] rounded-full px-2.5 py-0.5 text-xs font-bold" data-testid="profile-token-pill">
                🪙 {u.tokens}
              </Link>
              <Link to="/referrals" className="brutal-border bg-white rounded-full px-2.5 py-0.5 text-xs font-bold font-mono" data-testid="profile-referral-pill">
                {u.referral_code}
              </Link>
              <span
                className={`brutal-border rounded-full px-2.5 py-0.5 text-xs font-bold ${u.show_up_score >= 80 ? "bg-[#C8E6C9]" : u.show_up_score >= 50 ? "bg-[#FFCC00]" : "bg-[#FF6A2C] text-white"}`}
                data-testid="profile-show-up-score"
                title="Reliability score — 5 consecutive show-ups reset to 100"
              >
                ✅ {u.show_up_score ?? 100} show-up
              </span>
            </div>
          </div>
          <button onClick={() => { if (editingBio) saveBio(); else setEditingBio(true); }} className="text-[#555555]" data-testid="profile-edit-toggle">
            {editingBio ? <Check className="w-4 h-4 text-[#34C759]" /> : <Edit3 className="w-4 h-4" />}
          </button>
        </div>

        <div className="mt-4">
          <div className="label-mono mb-1">Bio</div>
          {editingBio ? (
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} onBlur={saveBio} rows={2} className="brutal-input text-sm" data-testid="profile-bio-input" autoFocus />
          ) : (
            <p className="text-sm">{u.bio}</p>
          )}
        </div>
      </div>

      {/* Personalisation */}
      <Section title="Your Grabb signal">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-[#FF6A2C]" />
          <span className="font-heading font-bold">Personalised from {u.history.joined_count} activities</span>
        </div>
        <div className="space-y-2">
          <Row k="Top activity types" v={u.history.top_types.map((t) => `${getActivityType(t).emoji} ${getActivityType(t).label}`).join(" · ")} />
          <Row k="You usually meet for" v={u.history.top_intents.map((i) => INTENTS.find((x) => x.id === i)?.label).join(" · ")} />
          <Row k="Favourite suburbs" v={u.history.favourite_suburbs.join(" · ")} />
          <Row k="Most-grabbed venue" v={u.history.favourite_venue} />
        </div>
        <p className="text-xs text-[#555555] mt-3">We use this to surface activities and people who match your actual pattern.</p>
      </Section>

      <Section title="Communities" hint="Tap to opt in or out — saves instantly.">
        <div className="flex flex-wrap gap-2">
          {COMMUNITIES.map((c) => {
            const on = u.communities.includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggleArr("communities", c.id)}
                disabled={saving}
                className={`brutal-border rounded-full px-3 py-1.5 text-sm font-bold transition-opacity ${on ? "ring-2 ring-[#0A0A0A]/40" : "opacity-50"}`}
                style={{ background: c.color }}
                data-testid={`profile-community-${c.id}`}
              >
                {c.emoji} {c.label}
              </button>
            );
          })}
        </div>
      </Section>

      {/* Local Legends */}
      <Section title="Local Legend">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-[#FFCC00]" />
          <span className="font-heading font-bold">You&apos;re a local at {MOCK_LOCAL_LEGENDS.length} venues</span>
        </div>
        <div className="space-y-2">
          {MOCK_LOCAL_LEGENDS.map((l) => (
            <div key={l.venue_id} className="brutal-border rounded-lg p-3 flex items-center justify-between" style={{ background: l.badge_color }} data-testid={`local-legend-${l.venue_id}`}>
              <div>
                <div className="font-heading font-bold">{l.venue_name}</div>
                <div className="text-xs text-[#0A0A0A]/70 font-mono">#{l.rank} regular · {l.checkins} check-ins</div>
              </div>
              <Trophy className="w-6 h-6" strokeWidth={2.5} />
            </div>
          ))}
        </div>
        <p className="text-xs text-[#555555] mt-3">Earn a Legend badge after 5+ check-ins at the same venue.</p>
      </Section>

      <Section title="Default intents" hint="Pick up to 3.">
        <div className="flex flex-wrap gap-2">
          {INTENTS.map((i) => {
            const on = u.default_intent.includes(i.id);
            const atMax = u.default_intent.length >= 3 && !on;
            return (
              <button
                key={i.id}
                onClick={() => toggleLimited("default_intent", i.id, 3)}
                disabled={saving || atMax}
                className={`brutal-border rounded-full px-3 py-1.5 text-sm font-bold transition-opacity ${on ? "bg-[#0A0A0A] text-white" : "bg-white opacity-80"} ${atMax ? "opacity-30 cursor-not-allowed" : ""}`}
                data-testid={`profile-intent-${i.id}`}
              >
                {i.emoji} {i.label}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Default vibes" hint="Pick up to 4.">
        <div className="flex flex-wrap gap-2">
          {VIBES.map((v) => {
            const on = u.default_vibe.includes(v.id);
            const atMax = u.default_vibe.length >= 4 && !on;
            return (
              <button
                key={v.id}
                onClick={() => toggleLimited("default_vibe", v.id, 4)}
                disabled={saving || atMax}
                className={`brutal-border rounded-full px-3 py-1.5 text-sm font-bold transition-opacity ${on ? "ring-2 ring-[#0A0A0A]/40" : "opacity-50"} ${atMax ? "opacity-30 cursor-not-allowed" : ""}`}
                style={{ background: v.color }}
                data-testid={`profile-vibe-${v.id}`}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Interests" hint="Tap to toggle.">
        <div className="flex flex-wrap gap-2">
          {ACTIVITY_TYPES.map((t) => {
            const on = u.interests.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => toggleArr("interests", t.id)}
                disabled={saving}
                className={`brutal-border rounded-full px-3 py-1.5 text-sm font-bold transition-opacity ${on ? "bg-[#FF6A2C] text-white" : "bg-white opacity-70"}`}
                data-testid={`profile-interest-${t.id}`}
              >
                {t.emoji} {t.label}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Quick links">
        <div className="grid grid-cols-2 gap-2">
          <Link to="/tokens" className="brutal-border bg-white rounded-lg p-3 text-sm font-bold" data-testid="profile-link-tokens">Tokens</Link>
          <Link to="/referrals" className="brutal-border bg-white rounded-lg p-3 text-sm font-bold" data-testid="profile-link-referrals">Referrals</Link>
          <Link to="/notifications" className="brutal-border bg-white rounded-lg p-3 text-sm font-bold" data-testid="profile-link-notifications">Notifications</Link>
          <Link to="/venues" className="brutal-border bg-white rounded-lg p-3 text-sm font-bold" data-testid="profile-link-venues">Venues</Link>
        </div>
      </Section>

      <div className="mt-6 flex gap-2">
        <button className="brutal-btn bg-white px-4 py-3 flex items-center gap-2 text-sm" data-testid="profile-settings">
          <Settings className="w-4 h-4" /> Settings
        </button>
        <button onClick={handleLogout} className="brutal-btn bg-white text-[#FF6A2C] px-4 py-3 flex items-center gap-2 text-sm" data-testid="profile-logout">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </div>
  );
};

const Section = ({ title, hint, children }) => (
  <div className="mt-5">
    <div className="flex items-center justify-between mb-2">
      <div className="label-mono">{title}</div>
      {hint && <span className="text-[10px] text-[#999] font-mono">{hint}</span>}
    </div>
    <div className="brutal-card p-4">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3 text-sm border-b border-[#E0E0E0] pb-2 last:border-0 last:pb-0">
    <span className="text-[#555555] shrink-0">{k}</span>
    <span className="font-bold text-right">{v}</span>
  </div>
);

export default Profile;
